import React, { useEffect, useMemo, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";
const WHEEL = 900;
const OUTSIDE = WHEEL / 2 - 10;
const INSIDE = OUTSIDE * 0.2;
const CONTENT_R = OUTSIDE * 0.55;
const OUTER_BORDER = 20;
const PREVIEW = 340;

const prepareImage = (img, rotation) => {
  const degrees = Number(rotation) || 0;
  if (!degrees) {
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  }
  const size = Math.max(img.naturalWidth, img.naturalHeight);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.translate(size / 2, size / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return { source: canvas, width: size, height: size };
};

const resolveUrl = (url) => {
  if (!url) return "";
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
};

const SliceWedgePreview = ({
  slices = [],
  activeIndex = 0,
  imageUrl = "",
  color = "#FF6B6B",
  zoom = 0.6,
  rotation = 0,
  offsetX = 0,
  offsetY = 0,
}) => {
  const canvasRef = useRef(null);
  const imagesRef = useRef({});
  const [loadGen, setLoadGen] = useState(0);

  const sliceData = useMemo(() => {
    return slices.map((slice, i) => {
      if (i === activeIndex) {
        return {
          color,
          imageUrl,
          zoom,
          rotation,
          offsetX,
          offsetY,
        };
      }
      return {
        color: slice?.color || "#3a3a3a",
        imageUrl: resolveUrl(
          slice?.background_image_url || slice?.image_url || ""
        ),
        zoom: slice?.image_zoom ?? 0.6,
        rotation: slice?.image_rotation ?? 0,
        offsetX: slice?.image_offset_x ?? 0,
        offsetY: slice?.image_offset_y ?? 0,
      };
    });
  }, [slices, activeIndex, color, imageUrl, zoom, rotation, offsetX, offsetY]);

  useEffect(() => {
    let cancelled = false;
    const urls = sliceData
      .map((s) => s.imageUrl)
      .filter(Boolean);
    const uniqueUrls = [...new Set(urls)];

    uniqueUrls.forEach((url) => {
      if (imagesRef.current[url]?.complete) return;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (cancelled) return;
        imagesRef.current = { ...imagesRef.current, [url]: img };
        setLoadGen((g) => g + 1);
      };
      img.onerror = () => {};
      img.src = url;
    });

    return () => {
      cancelled = true;
    };
  }, [sliceData]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const scale = PREVIEW / WHEEL;
    const cx = PREVIEW / 2;
    const cy = PREVIEW / 2;
    const outside = OUTSIDE * scale;
    const inside = INSIDE * scale;
    const border = OUTER_BORDER * scale;
    const imageOuter = Math.max(inside + 2, outside - border);
    const count = Math.max(sliceData.length, 1);
    const arc = (2 * Math.PI) / count;
    const index = Math.min(Math.max(activeIndex, 0), count - 1);

    ctx.clearRect(0, 0, PREVIEW, PREVIEW);

    for (let i = 0; i < count; i += 1) {
      const start = i * arc;
      const sliceColor = sliceData[i]?.color || "#3a3a3a";
      ctx.beginPath();
      ctx.arc(cx, cy, outside, start, start + arc, false);
      ctx.arc(cx, cy, inside, start + arc, start, true);
      ctx.closePath();
      ctx.fillStyle = sliceColor;
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.35)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    for (let i = 0; i < count; i += 1) {
      const sd = sliceData[i];
      if (!sd?.imageUrl) continue;
      const loaded = imagesRef.current[sd.imageUrl];
      if (!loaded || !loaded.complete) continue;

      const prepared = prepareImage(loaded, sd.rotation);
      const start = i * arc;
      const mid = start + arc / 2;
      const imgH = 200 * (Number(sd.zoom) || 0.6) * scale;
      const imgW = (prepared.width / prepared.height) * imgH;
      const contentX = cx + Math.cos(mid) * CONTENT_R * scale;
      const contentY = cy + Math.sin(mid) * CONTENT_R * scale;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(
        cx + Math.cos(start) * (inside + 2),
        cy + Math.sin(start) * (inside + 2)
      );
      ctx.lineTo(
        cx + Math.cos(start) * imageOuter,
        cy + Math.sin(start) * imageOuter
      );
      ctx.arc(cx, cy, imageOuter, start, start + arc, false);
      ctx.lineTo(
        cx + Math.cos(start + arc) * (inside + 2),
        cy + Math.sin(start + arc) * (inside + 2)
      );
      ctx.arc(cx, cy, inside + 2, start + arc, start, true);
      ctx.closePath();
      ctx.clip();
      ctx.translate(contentX, contentY);
      ctx.rotate(mid + Math.PI / 2);
      const drawX = (imgW + (Number(sd.offsetX) || 0) * scale) / -2;
      const drawY = -(imgH - 90 * scale + (Number(sd.offsetY) || 0) * scale) / 2;
      ctx.drawImage(prepared.source, drawX, drawY, imgW, imgH);
      ctx.restore();

      if (i !== index) {
        ctx.save();
        ctx.globalAlpha = 0.15;
        ctx.beginPath();
        ctx.moveTo(
          cx + Math.cos(start) * (inside + 2),
          cy + Math.sin(start) * (inside + 2)
        );
        ctx.lineTo(
          cx + Math.cos(start) * imageOuter,
          cy + Math.sin(start) * imageOuter
        );
        ctx.arc(cx, cy, imageOuter, start, start + arc, false);
        ctx.lineTo(
          cx + Math.cos(start + arc) * (inside + 2),
          cy + Math.sin(start + arc) * (inside + 2)
        );
        ctx.arc(cx, cy, inside + 2, start + arc, start, true);
        ctx.closePath();
        ctx.fillStyle = "#000";
        ctx.fill();
        ctx.restore();
      }
    }

    ctx.beginPath();
    ctx.arc(cx, cy, outside - border / 2, 0, Math.PI * 2);
    ctx.strokeStyle = "#FFD700";
    ctx.lineWidth = border;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(cx, cy, imageOuter, index * arc, index * arc + arc, false);
    ctx.arc(cx, cy, inside + 2, index * arc + arc, index * arc, true);
    ctx.closePath();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.5;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(cx, cy, inside, 0, Math.PI * 2);
    ctx.fillStyle = "#111";
    ctx.fill();
  }, [sliceData, activeIndex, loadGen]);

  return (
    <canvas
      ref={canvasRef}
      width={PREVIEW}
      height={PREVIEW}
      style={{
        width: PREVIEW,
        height: PREVIEW,
        display: "block",
        margin: "0 auto",
      }}
    />
  );
};

export default SliceWedgePreview;
